/* eslint-disable import/prefer-default-export */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { RefObject, useEffect, useRef, useState } from "react";
import { Icon, Video } from "@components";
import classNames from "classnames";
import { Asset } from "contentful";

import { SiteImage } from "../../../lib/components";
import { isVideo } from "../../../lib/components/util";
import selectors from "../selectors";

interface ImageGalleryMediaProps {
  media: Asset;
  objectFit: "cover" | "contain";
  bigScreen: boolean;
}

export const prepLocation = (
  xin: number,
  yin: number,
  ref: RefObject<HTMLDivElement>
) => {
  const divWidth = ref.current?.clientWidth;
  const divHeight = ref.current?.clientHeight;
  const x = divWidth ? `${(xin * -100) / divWidth}%` : "0";
  const y = divHeight ? `${(yin * -100) / divHeight}%` : "0";
  return { x, y };
};

export function ImageGalleryMedia({
  media,
  objectFit,
  bigScreen,
}: ImageGalleryMediaProps) {
  const [playing, setPlaying] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [imageLocationX, setImageLocationX] = useState("0");
  const [imageLocationY, setImageLocationY] = useState("0");

  const videoRef = useRef<HTMLVideoElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const clickPlayPause = () => {
    if (playing) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.play();
    }
    setPlaying((p) => !p);
  };

  const expandImage = (e: {
    nativeEvent: { offsetX: number; offsetY: number };
  }) => {
    if (zoomed) {
      setImageLocationX("0");
      setImageLocationY("0");
    } else {
      const xy = prepLocation(
        e.nativeEvent.offsetX,
        e.nativeEvent.offsetY,
        imageWrapperRef
      );
      setImageLocationX(xy.x);
      setImageLocationY(xy.y);
    }
    setZoomed((z) => !z);
  };

  useEffect(() => {
    const mouseMove = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const xy = prepLocation(
        e.offsetX + e.movementX,
        e.offsetY + e.movementY,
        imageWrapperRef
      );
      setImageLocationX(xy.x);
      setImageLocationY(xy.y);
    };

    const ref = imageWrapperRef.current;
    if (zoomed && bigScreen) {
      ref?.addEventListener("mousemove", mouseMove); // toggle for mouse move
    }

    return () => {
      ref?.removeEventListener("mousemove", mouseMove);
    };
  }, [zoomed, bigScreen]);

  return isVideo(media) ? (
    <>
      <div
        style={{
          aspectRatio: `${media.fields.file.details.image?.width} / ${media.fields.file.details.image?.height}`,
        }}
        className={classNames(
          "w-full h-full m-auto",
          {
            "object-contain": objectFit === "contain",
            "object-cover": objectFit === "cover",
          },
          bigScreen && "hover:cursor-pointer"
        )}
        onClick={() => bigScreen && clickPlayPause()}
        data-testid={selectors.videoWrapper}
      >
        <Video
          sources={[
            {
              src: media.fields.file.url,
              type: media.fields.file.contentType,
            },
          ]}
          autoPlay
          muted
          loop
          className={bigScreen ? "hover:cursor-pointer" : undefined}
          ref={videoRef}
          objectFit={objectFit}
          data-testid={selectors.video}
          // poster={media.preview} /* do this later */
        />
      </div>
      <span
        className={classNames(
          "absolute transition-opacity duration-300 bg-white opacity-0 text-md group-hover:opacity-100 rounded-full pl-[1px]",
          bigScreen
            ? "bottom-0 top-0 right-0 left-0 m-auto w-8 h-8"
            : "bottom-2 right-4 w-4 h-4"
        )}
      >
        <span
          role="button"
          className="flex items-center justify-center w-full h-full"
          onClick={(e) => {
            clickPlayPause();
            e.stopPropagation();
          }}
        >
          <Icon
            name={playing ? "pause" : "play"}
            className={classNames(
              "text-charcoal",
              bigScreen ? "h-6 w-6" : "h-4 w-4"
            )}
            size="full"
          />
        </span>
      </span>
    </>
  ) : (
    <div
      onClick={expandImage}
      className={classNames("h-full", {
        "hover:cursor-pointer": !bigScreen,
        "hover:cursor-zoom-in": bigScreen && !zoomed,
        "h-[200%] w-[200%] absolute hover:cursor-zoom-out": bigScreen && zoomed,
      })}
      style={{
        top: imageLocationY,
        left: imageLocationX,
      }}
      ref={imageWrapperRef}
      data-testid={selectors.imageWrapper}
    >
      <SiteImage
        src={media.fields.file.url}
        alt={media.fields.title}
        objectFit={objectFit}
      />
      {!bigScreen && (
        <span className="absolute transition-opacity duration-300 bg-white opacity-0 text-md group-hover:opacity-100 rounded-full flex justify-center items-center pl-[1px] bottom-2 right-4 w-5 h-5">
          <Icon name="search" className="w-3 h-3 text-charcoal" size="full" />
        </span>
      )}
    </div>
  );
}
